var __extends = (this && this.__extends) || (function () {
    var extendStatics = function (d, b) {
        extendStatics = Object.setPrototypeOf ||
            ({ __proto__: [] } instanceof Array && function (d, b) { d.__proto__ = b; }) ||
            function (d, b) { for (var p in b) if (Object.prototype.hasOwnProperty.call(b, p)) d[p] = b[p]; };
        return extendStatics(d, b);
    };
    return function (d, b) {
        if (typeof b !== "function" && b !== null)
            throw new TypeError("Class extends value " + String(b) + " is not a constructor or null");
        extendStatics(d, b);
        function __() { this.constructor = d; }
        d.prototype = b === null ? Object.create(b) : (__.prototype = b.prototype, new __());
    };
})();
var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
/**
 * @module cercaliagl/control/Traffic
 */
import Control from './Control';
import MapControls from './MapControls';
import MapTypes from '../MapTypes';
import { i18n } from '../dictionary';
import VControlButton from '../view/VControlButton';
import ServicePois from '../service/Pois.js';
import VTraffic, { EventType as TrafficEventType, Buttons } from '../view/VTraffic.js';
import TrafficLiveLayer from '../layer/TrafficLive';
import TrafficForecastLayer from '../layer/TrafficForecast';
import { IMG, KEY } from '../globals';
import PoisMap from '../MapPois';
import Marker from '../Marker';
import Icon from '../Icon';
import LonLat from '../LonLat';
import Popup from '../Popup';
import Servers from '../servers';
import WMS from '../layer/WMS';
import { BaseLayerType, ReferenceLayer } from '../MapStyler';
/**
 * @enum {string}
 */
var Type = {
    LIVE: 'live',
    FORECAST: 'forecast',
};
/**
 * @enum {Array.<string>}
 */
var PoisCategories = {
    SCT_DGT_AFFECTATIONS: ['D00A2', 'D00A5', '082A2', '082A5'],
    CAMERAS: ['D00CAM'],
    ROADWORKS: ['D00A3', '082A3'],
};
/**
 * @type {Object.<string, string>}
 * @api
 */
export var Options = {
    /**
     * @api
     */
    LIVE: 'live',
    /**
     * @api
     */
    FORECAST: 'forecast',
    /**
     * @api
     */
    CAMERAS: 'cameras',
    /**
     * @api
     */
    MILESTONE: 'milestone',
    /**
     * @api
     */
    ROADWORKS: 'roadworks',
};
/**
 * @classdesc
 * <b>Javascript ES5</b>
 * ```js
 * const control = new cercaliagl.control.Traffic();
 * ```
 * <br/>
 * This control allow to display real traffic and forecast traffic.
 * <br/>
 * - In real traffic most roads display linestrings over them. In the bottom right dialog you can see the legend.
 *   Traffic affectations like accidents, network managements are displayed too.
 * - In forecast traffic you can adjust the date and the forecast time.
 */
var Traffic = /** @class */ (function (_super) {
    __extends(Traffic, _super);
    /**
     * @constructor
     * @api
     */
    function Traffic() {
        var _this = this;
        var controlButton = new VControlButton('traffic');
        var element = controlButton.getElement();
        var trafficDialog = new VTraffic();
        _this = _super.call(this, {
            element: element,
            className: 'cercaliagl.control.Traffic',
            name: MapControls.TRAFFIC,
            dialog: trafficDialog.getElement(),
        }) || this;
        /**
         * @private
         * @type {boolean}
         */
        _this.enabled_ = false;
        /**
         * @private
         * @type {VControlButton}
         */
        _this.controlButton_ = controlButton;
        /**
         * @private
         * @type {VTraffic}
         */
        _this.trafficDialog_ = trafficDialog;
        /**
         * @private
         * @type {TrafficLiveLayer}
         */
        _this.layerTrafficHDFlow_ = null;
        /**
         * @private
         * @type {TrafficForecastLayer}
         */
        _this.layerForecastTraffic_ = null;
        /**
         * @private
         * @type {string}
         */
        _this.currentType_ = Type.LIVE;
        /**
         * @private
         * @type {HTMLElement}
         */
        _this.element_ = element;
        /**
         * @private
         * @type {PoisMap}
         */
        _this.mapPois_ = null;
        /**
         * @private
         * @type {Array.<Popup>}
         */
        _this.activeCameraPopups_ = [];
        /**
         * @private
         * @type {WMS|null}
         */
        _this.milestoneLayer_ = null;
        _this.initialize_();
        return _this;
    }
    /**
     * @private
     */
    Traffic.prototype.initialize_ = function () {
        var _this = this;
        this.controlButton_.setTooltip(i18n('tooltipTraffic'));
        this.element_.addEventListener('click', this.handleClick_.bind(this));
        this.poisService_ = new ServicePois(/** @type {import('../service/Pois').PoisOptions} */ ({
            searchByScale: true,
            categories: [],
        }));
        this.trafficDialog_.on(TrafficEventType.CHANGE_MODE, this.toggleTrafficType.bind(this));
        this.trafficDialog_.on(TrafficEventType.FORECAST_CHANGE, function (forecastOptions) { return _this.updateForecastParams_(forecastOptions); });
        this.trafficDialog_.on(TrafficEventType.ENABLE_BUTTON, function (button) { return _this.handleEnableButton_(button); });
        this.trafficDialog_.on(TrafficEventType.DISABLE_BUTTON, function (button) { return _this.handleDisableButton_(button); });
    };
    /**
     * @private
     */
    Traffic.prototype.handleClick_ = function () {
        if (!this.enabled_) {
            this.enable();
        }
        else {
            this.disable();
        }
    };
    /**
     * Enable traffic control. Default enabled mode is live traffic.
     * @override
     * @api
     */
    Traffic.prototype.enable = function () {
        this.enabled_ = true;
        this.controlButton_.enable();
        this.trafficDialog_.show();
        this.createLiveTraffic_();
        this.initializeMapPois_();
    };
    /**
     * Disable traffic control.
     * @override
     * @api
     */
    Traffic.prototype.disable = function () {
        this.enabled_ = false;
        this.controlButton_.disable();
        this.trafficDialog_.hide();
        this.removeLiveTraffic_();
        this.removeForecastTraffic_();
        this.deinitializeMapPois_();
    };
    /**
     * @private
     */
    Traffic.prototype.initializeMapPois_ = function () {
        this.mapPois_ = new PoisMap({
            map: this.getCercaliaMap(),
            createMarkersFunction: this.createMarkersFunction_.bind(this),
        });
        this.mapPois_.enable();
    };
    /**
     * @private
     */
    Traffic.prototype.deinitializeMapPois_ = function () {
        if (this.mapPois_) {
            this.mapPois_.disable();
            this.mapPois_ = null;
        }
    };
    /**
     * @private
     */
    Traffic.prototype.createLiveTraffic_ = function () {
        this.removeForecastTraffic_();
        if (!this.layerTrafficHDFlow_) {
            var cercaliaMap = this.getCercaliaMap();
            this.layerTrafficHDFlow_ = new TrafficLiveLayer();
            cercaliaMap.addLayer(this.layerTrafficHDFlow_);
            this.moveLayers_();
        }
    };
    /**
     * @private
     */
    Traffic.prototype.createForecastTraffic_ = function () {
        this.removeLiveTraffic_();
        if (!this.layerForecastTraffic_) {
            var cercaliaMap = this.getCercaliaMap();
            var forecastOptions = this.trafficDialog_.getForecastOptions();
            this.layerForecastTraffic_ = new TrafficForecastLayer(__assign({}, forecastOptions));
            cercaliaMap.addLayer(this.layerForecastTraffic_);
            this.moveLayers_();
        }
    };
    /**
     * @private
     */
    Traffic.prototype.createMilestoneLayer_ = function () {
        if (!this.milestoneLayer_) {
            this.milestoneLayer_ = new WMS({
                url: Servers.pks,
                tileSize: 512,
                params: {
                    width: 512,
                    height: 512,
                    layers: 'cercalia',
                    projection: 'EPSG:3857',
                },
            });
            this.getCercaliaMap().addLayer(this.milestoneLayer_);
        }
    };
    /**
     * @private
     */
    Traffic.prototype.removeLiveTraffic_ = function () {
        var cercaliaMap = this.getCercaliaMap();
        if (this.layerTrafficHDFlow_) {
            this.layerTrafficHDFlow_.cleanLayer();
            cercaliaMap.removeLayer(this.layerTrafficHDFlow_);
            this.layerTrafficHDFlow_ = null;
        }
    };
    /**
     * @private
     */
    Traffic.prototype.removeForecastTraffic_ = function () {
        var cercaliaMap = this.getCercaliaMap();
        if (this.layerForecastTraffic_) {
            cercaliaMap.removeLayer(this.layerForecastTraffic_);
            this.layerForecastTraffic_ = null;
        }
    };
    /**
     * @private
     */
    Traffic.prototype.removeMilestoneLayer_ = function () {
        if (this.milestoneLayer_) {
            var cercaliaMap = this.getCercaliaMap();
            cercaliaMap.removeLayer(this.milestoneLayer_);
            this.milestoneLayer_ = null;
        }
    };
    /**
     * @param {string} type Type `live` or `forecast`.
     */
    Traffic.prototype.toggleTrafficType = function (type) {
        if (type === Type.LIVE) {
            this.removeForecastTraffic_();
            this.createLiveTraffic_();
            this.currentType_ = type;
        }
        else if (type === Type.FORECAST) {
            this.removeLiveTraffic_();
            this.createForecastTraffic_();
            this.currentType_ = type;
        }
    };
    /**
     * @private
     * @param {import("../view/VTraffic.js").ForecastOptions} forecastOptions Forecast options
     */
    Traffic.prototype.updateForecastParams_ = function (forecastOptions) {
        if (this.layerForecastTraffic_) {
            this.layerForecastTraffic_.updateParams(forecastOptions.departuredayofweek, forecastOptions.departurefrommidnight);
        }
    };
    /**
     * @private
     */
    Traffic.prototype.moveLayers_ = function () {
        var layersId;
        var cercaliaMap = this.getCercaliaMap();
        var mapStyler = cercaliaMap.getMapStyler();
        var currentStyle = mapStyler.getStyles();
        if (mapStyler.getBaseLayerType() === BaseLayerType.VECTOR_TILES && currentStyle.name.toLocaleUpperCase() !== MapTypes.SILVER.toLocaleUpperCase() && currentStyle.name.toLocaleUpperCase() !== MapTypes.DARK.toLocaleUpperCase()) {
            var layerManager = cercaliaMap.getLayerManager();
            if (this.layerTrafficHDFlow_) {
                layersId = this.layerTrafficHDFlow_.getLayers().map(function (layer) { return layer.id; });
            }
            else {
                layersId = this.layerForecastTraffic_.getLayers().map(function (layer) { return layer.id; });
            }
            layerManager.moveLayers(layersId, ReferenceLayer.BEFORE_LABELS);
        }
    };
    /**
     * Enable traffic options. Possible values:
     * - cercaliagl.control.Traffic.Options.LIVE<br/>
     * - cercaliagl.control.Traffic.Options.FORECAST<br/>
     * - cercaliagl.control.Traffic.Options.CAMERAS<br/>
     * - cercaliagl.control.Traffic.Options.MILESTONE<br/>
     * - cercaliagl.control.Traffic.Options.ROADWORKS<br/>
     * @param {string} optionId Option to enable.
     * @api
     */
    Traffic.prototype.enableOption = function (optionId) {
        if (optionId === Options.LIVE) {
            this.createLiveTraffic_();
        }
        else if (optionId === Options.FORECAST) {
            this.createForecastTraffic_();
        }
        else {
            this.trafficDialog_.enableButton(optionId);
        }
    };
    /**
     * @private
     * @param {string} buttonName Button name
     */
    Traffic.prototype.handleEnableButton_ = function (buttonName) {
        if (buttonName === Buttons.CAMERAS) {
            this.toggleCameras_(true);
        }
        else if (buttonName === Buttons.MILESTONE) {
            this.toggleMilestone_(true);
        }
        else if (buttonName === Buttons.ROADWORKS) {
            this.toggleRoadworks_(true);
        }
        else {
            console.error("Unknown button with id=".concat(buttonName)); // eslint-disable-line no-console
        }
    };
    /**
     * @private
     * @param {string} buttonName Button name
     */
    Traffic.prototype.handleDisableButton_ = function (buttonName) {
        if (buttonName === Buttons.CAMERAS) {
            this.toggleCameras_(false);
        }
        else if (buttonName === Buttons.MILESTONE) {
            this.toggleMilestone_(false);
        }
        else if (buttonName === Buttons.ROADWORKS) {
            this.toggleRoadworks_(false);
        }
        else {
            console.error("Unknown button with id=".concat(buttonName)); // eslint-disable-line no-console
        }
    };
    /**
     * @private
     * @param {boolean} enabled `true` or `false`.
     */
    Traffic.prototype.toggleCameras_ = function (enabled) {
        if (enabled) {
            this.mapPois_.appendCategories(PoisCategories.CAMERAS);
        }
        else {
            this.mapPois_.removeCategories(PoisCategories.CAMERAS);
        }
    };
    /**
     * @private
     * @param {boolean} enabled `true` or `false`.
     */
    Traffic.prototype.toggleMilestone_ = function (enabled) {
        if (enabled) {
            this.createMilestoneLayer_();
        }
        else {
            this.removeMilestoneLayer_();
        }
    };
    /**
     * @private
     * @param {boolean} enabled `true` or `false`.
     */
    Traffic.prototype.toggleRoadworks_ = function (enabled) {
        if (enabled) {
            this.mapPois_.appendCategories(PoisCategories.ROADWORKS);
        }
        else {
            this.mapPois_.removeCategories(PoisCategories.ROADWORKS);
        }
    };
    /**
     * Create a list of markers to paint from server json, a BBOX and zoom level.
     * @private
     * @param {Array.<import('../servers').PoisResultDataPoi>} data Json downloaded from server.
     * @param {import('../Bounds').default} bounds BBOX (bounds).
     * @param {number} zoom Zoom level.
     * @return {Array.<Marker>} Markers array.
     */
    Traffic.prototype.createMarkersFunction_ = function (pois) {
        var markers = [];
        var cercaliaMap = this.getCercaliaMap();
        for (var i = 0; i < pois.length; i += 1) {
            var poi = pois[i];
            var srcIcon = void 0;
            var drawMarker = true;
            var categoryId = poi.category_id;
            if (PoisCategories.SCT_DGT_AFFECTATIONS.indexOf(categoryId) !== -1) {
                var type = this.getSctDgtLevel_(categoryId, poi.info.value);
                var zoom = cercaliaMap.getZoom();
                if (this.filterSctDgtAffectations_(type, zoom) && this.filterOnlyCatSCT_(poi)) {
                    srcIcon = this.getIconSctDgt_(categoryId, poi, type);
                }
                else {
                    drawMarker = false;
                }
            }
            else if (PoisCategories.ROADWORKS.indexOf(categoryId) !== -1) {
                srcIcon = "".concat(IMG, "/traffic/roadworks.png");
            }
            else if (PoisCategories.CAMERAS.indexOf(categoryId) !== -1) {
                srcIcon = "".concat(IMG, "/traffic/D00CAM.png");
            }
            if (drawMarker && poi.category_id) {
                var marker = new Marker(/** @type {import('../Marker').MarkerOptions} */ ({
                    position: new LonLat(poi.coord.x, poi.coord.y),
                    icon: new Icon(/** @type {import('../Icon').IconOptions} */ ({
                        src: srcIcon,
                    })),
                    onClick: this.handleClickTrafficMarker_.bind(this, poi),
                }));
                markers.push(marker);
            }
        }
        return markers;
    };
    /**
     * @private
     * @param {import('../servers').PoiEntity} poi Poi.
     * @param {Marker} marker Marker clicked.
     */
    Traffic.prototype.handleClickTrafficMarker_ = function (poi, marker) {
        var popup;
        if (PoisCategories.ROADWORKS.indexOf(poi.category_id) !== -1) {
            popup = Traffic.createRoadworksPopup(poi, marker);
        }
        else if (PoisCategories.CAMERAS.indexOf(poi.category_id) !== -1) {
            popup = Traffic.createCameraPopup(poi, marker);
            this.activeCameraPopups_.push(popup);
        }
        else if (poi.category_id && PoisCategories.SCT_DGT_AFFECTATIONS.indexOf(poi.category_id) !== -1) {
            popup = Traffic.createPopupAffectationSctDgt(poi, marker);
        }
        if (popup) {
            marker.addPopup(popup);
            marker.openPopup();
        }
    };
    /**
     * @private
     * @param {import('../servers').PoiEntity} poi Poi.
     * @param {Marker} marker Marker
     * @return {Popup} Popup.
     */
    Traffic.createRoadworksPopup = function (poi, marker) {
        var info = poi.info.value;
        var content = "\n      <div>\n        <span><b></b>".concat(info, "</span><br/>\n      </div>");
        var popup = marker.getPopup();
        if (!popup) {
            popup = new Popup(/** @type {import('../Popup').PopupOptions} */ ({
                title: i18n('roadWorks'),
                content: content,
                offset: [0, -24],
            }));
        }
        else {
            popup.setContent(content);
        }
        return popup;
    };
    /**
     * @private
     * @param {import('../servers').PoiEntity} poi Poi info.
     * @param {Marker} marker Marker
     * @return {Popup} Popup.
     */
    Traffic.createCameraPopup = function (poi, marker) {
        var arrInfo = poi.info.value.split('|');
        var title = arrInfo[1];
        var font = arrInfo[2];
        var origSrc = arrInfo[3];
        var src = origSrc + (origSrc.indexOf('?') === -1 ? '?time=' : '&time=') + Date.now();
        var proxySrc = "".concat(Servers.proxy, "?url=").concat(encodeURIComponent(src), "&key=").concat(KEY);
        var content = "\n      <div>\n        <img style=\"width:240px;height:176px\" src=\"".concat(proxySrc, "\">\n        <br/>\n        <div>").concat(font, "</div>\n      </div>");
        var popup = marker.getPopup();
        if (!popup) {
            popup = new Popup(/** @type {import('../Popup').PopupOptions} */ ({
                title: title,
                content: content,
                maxWidth: 'none',
                offset: [0, -24],
            }));
        }
        else {
            popup.setTitle(title);
            popup.setContent(content);
        }
        return popup;
    };
    /**
     * @private
     * @param {import('../servers').PoiEntity} poi Poi info.
     * @param {Marker} marker Marker
     * @return {Popup} Popup.
     */
    Traffic.createPopupAffectationSctDgt = function (poi, marker) {
        var name = poi.name.value;
        var info = poi.info.value;
        var strHtml = "\n      <div class=\"affectation\">\n        <p>".concat(info, "</p>\n      </div>");
        var popup = marker.getPopup();
        if (popup) {
            popup = new Popup(/** @type {import('../Popup').PopupOptions} */ ({
                title: name,
                content: strHtml,
                offset: [0, -24],
            }));
        }
        else {
            popup.setTitle(name);
            popup.setContent(strHtml);
        }
        return popup;
    };
    return Traffic;
}(Control));
export default Traffic;
//# sourceMappingURL=Traffic.js.map